import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "Common/Globle/Spinner";
import { validateArrayForm, validateField } from "utils/validation";
import {
    ReservationDetailsRules,
    RoomFieldsRules,
    AdvanceDetailsRules,
    DormitoryRules
} from "validation/ValidationRules";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import {
    updateRoomBooking,
    getAllPaymentMethods,
    getAllRoomTypes,
    getBookingById,
} from 'adminApi/RoomBookingApi/RoomBooking';
import RoomBookingForm from "./Components/EditRoomBookingForm";
import useFetchAllData from "../../Components/useFetchAllData";
import { getAllowedFieldsByType, validateAdvanceRow } from "../../HELPERS/Helpers"

import fields from "../json/RoomBookingFields.json";

// ---------------------------------------
// HELPERS
// ---------------------------------------

const normalizeDate = (str) => (str ? String(str).slice(0, 10) : "");

const { form_data } = fields;
const {
    Reservation_Details,
    RoomFields,
    extraFields,
    Advance_Details,
    Rent_Info,
    parkingFields
} = form_data

const data = [
    ...Reservation_Details,
    ...parkingFields
]

const roomFields = [
    ...RoomFields,
    ...extraFields,
    ...Rent_Info
]

// ---------------------------------------
// JSON-DRIVEN FIELD-OWNERSHIP SETS
// (used to strip leaked/duplicate keys before sending to the backend —
//  no manual field-name lists, fully driven by RoomBookingFields.json)
// ---------------------------------------

// Field names that belong to PaymentDetail rows only (e.g. payment_method,
// exclusion logic below updates automatically.
const paymentFieldNames = new Set(Advance_Details.map((f) => f.name));

// Field names that belong to room rows only (RoomFields + extraFields + Rent_Info).
const roomFieldNames = new Set(roomFields.map((f) => f.name));

const flatInitialValues = Object.fromEntries(
    data.map((f) => [
        f.name,
        f.name === "total_vehicle" ? 0 : ""
    ])
);

const emptyRoom = {
    ...Object.fromEntries(roomFields.map((f) => [f.name, ""])),
    capacity: "",
    accommodation_type: "ROOM",
    avalible_beds: "",
    parking_rate: "",
    Unic_key: crypto.randomUUID()
};

// next to emptyRoom
const emptyPaymentDetail = {
    ...Object.fromEntries(Advance_Details.map((f) => [f.name, ""])),
    Unic_key: crypto.randomUUID(),
    booking_id: "",
};


const initialValues = {
    ...flatInitialValues,
    customer_id: "",
    full_guest_name: "",
    country_dial_code: "",
    mobile: "",
    advance_amount: "",
    is_apply_balance: false,
    avalible_balance: "",
    is_apply_balance_amount: "",
    is_prev_apply_balance: "",
    prev_used_balance: "",
    rooms: [emptyRoom],
}


const EditRoomBooking = () => {

    const navigate = useNavigate();
    const { id } = useParams()

    const [ChildrenMinAge, setChildrenMinAge] = useState(null);
    const [ChildrenMaxAge, setChildrenMaxAge] = useState(null);
    const [loadingFetch, setLoadingFetch] = useState(false);

    const { data: roomTypesOption = [], } = useFetchAllData({ apiCall: getAllRoomTypes });
    const { data: paymentMethodTypesOption = [] } = useFetchAllData({ apiCall: getAllPaymentMethods });
    const [taxConfig, setTaxConfig] = useState({});


    const clonedInitialValues = {
        ...initialValues,
        rooms: [{ ...initialValues.rooms[0] }],
    };

    const {
        values,
        setValues,
        errors,
        handleChange,
        setErrors
    } = useFormChange(clonedInitialValues, ReservationDetailsRules);

    // ---------------------------------------
    // LOAD BOOKING DATA
    // ---------------------------------------

    useEffect(() => {
        if (!id) return;

        const fetchBooking = async () => {
            setLoadingFetch(true);

            try {
                const res = await getBookingById(id);
                const data = res?.data?.data || {};


                setChildrenMaxAge(data.children_max_age);
                setChildrenMinAge(data.children_min_age);

                setTaxConfig(data.tax_config)


                // Use the functional update form of setValues so we always merge
                // with the latest state instead of a stale closure value of `values`.
                setValues((prevValues) => {
                    const mergedValues = {
                        ...prevValues,
                        ...data,
                        checkin_date: normalizeDate(data.checkin_date),
                        checkout_date: normalizeDate(data.checkout_date),
                        rooms: data.rooms?.length ? data.rooms.map(room => ({
                            ...room,
                            extracheck_in_date: normalizeDate(room.extracheck_in_date),
                            extracheck_out_date: normalizeDate(room.extracheck_out_date),
                            Unic_key: crypto.randomUUID()
                        }))
                            : [{ ...initialValues.rooms[0] }],
                        PaymentDetail: data.payment_transactions?.length
                            ? data.payment_transactions.map((txn) => ({
                                ...emptyPaymentDetail,
                                ...txn,
                                transaction_date: normalizeDate(txn.transaction_date),
                                Unic_key: crypto.randomUUID(),
                            }))
                            : [{ ...emptyPaymentDetail, Unic_key: crypto.randomUUID() }],
                    };

                    return mergedValues;
                });

            } catch (error) {
                console.error("Error fetching booking:", error);
            } finally {
                setLoadingFetch(false);
            }
        };

        fetchBooking();
    }, [id]);


    const FindRool = (rooms) => {
        const roomRules = {};

        rooms.forEach((room, index) => {
            roomRules[index] = {
                ...(room.accommodation_type === "ROOM"
                    ? RoomFieldsRules
                    : DormitoryRules)
            };
        });

        return roomRules;
    };



    // ---------------------------------------
    // SUBMIT HANDLER
    // ---------------------------------------

    const { handleSubmit, loading: loadingSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();

            // Complex/structured fields handled separately below
            const excludedFields = [
                "rooms",
                "balance",
                "PaymentDetail",
                "tax_details",
                "vehicle_numbers",
            ];

            Object.entries(formValues).forEach(([key, value]) => {
                if (excludedFields.includes(key)) return;

                // Skip keys that belong to PaymentDetail or Room rows
                if (key === "advance_amount") {
                    formData.append(key, value ?? "");
                }

                if (paymentFieldNames.has(key) || roomFieldNames.has(key)) return;

                formData.append(key, value ?? "");
            });

            // Allowed room fields by accommodation type
            const allowedByType = getAllowedFieldsByType(form_data);

            const cleanedRooms = formValues.rooms.map((room) => {
                const allowedFields = new Set([
                    ...(allowedByType[room.accommodation_type] || []),
                    "accommodation_type",
                    "book_details_id",
                    "complementary_price_type",
                ]);

                const roomWithExtras = {
                    ...room,
                    extracheck_in_date:
                        room.extracheck_in_date || formValues.checkin_date || "",
                    extracheck_out_date:
                        room.extracheck_out_date || formValues.checkout_date || "",
                };

                return Object.fromEntries(
                    Object.entries(roomWithExtras).filter(([key]) =>
                        allowedFields.has(key)
                    )
                );
            });

            // Keep only valid payment rows
            const paymentDetail = (formValues.PaymentDetail || [])
                .filter((p) => p.payment_method || p.advance_amount)
                .map((p) => ({
                    booking_id: p.booking_id,
                    ...Object.fromEntries(
                        Object.entries(p).filter(([key]) =>
                            paymentFieldNames.has(key)
                        )
                    ),
                }));

            const tax_details = (formValues.tax_details || []).map(
                ({ Unic_key, ...rest }) => rest
            );

            formData.append("rooms", JSON.stringify(cleanedRooms));
            formData.append("balance", JSON.stringify(formValues.balance || {}));
            formData.append("PaymentDetail", JSON.stringify(paymentDetail));
            formData.append("tax_details", JSON.stringify(tax_details));
            formData.append(
                "vehicle_numbers",
                JSON.stringify(formValues.vehicle_numbers || [])
            );

            return updateRoomBooking({ id, data: formData });
        },

        (vals) => {
            const allrules = {
                ...ReservationDetailsRules,
                rooms: FindRool(vals.rooms),
                PaymentDetail: { ...AdvanceDetailsRules },
            };

            const validationErrors = validateArrayForm(vals, allrules);

            const isLocked = (row) =>
                row?.booking_id !== undefined &&
                row?.booking_id !== null &&
                row?.booking_id !== "";

            // Skip validation for existing payment rows.
            // For new rows, Cash doesn't require Transaction ID.
            const advanceRowErrors = (vals.PaymentDetail || []).map((row) =>
                isLocked(row)
                    ? {}
                    : validateAdvanceRow(row, paymentMethodTypesOption)
            );

            const hasAnyAdvanceError = advanceRowErrors.some(
                (e) => Object.keys(e).length > 0
            );

            if (hasAnyAdvanceError) {
                validationErrors.PaymentDetail = (vals.PaymentDetail || []).map(
                    (_, index) => ({
                        ...(validationErrors.PaymentDetail?.[index] || {}),
                        ...advanceRowErrors[index],
                    })
                );
            }

            setErrors(validationErrors);

            return validationErrors;
        },

        () => navigate(-1),

        "Room Booking Updated Successfully"
    );


    return (
        <>
            {(loadingSubmit || loadingFetch) && <Spinner isSpinner={true} />}

            <RoomBookingForm
                values={values}
                setValues={setValues}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                roomTypesOption={roomTypesOption}
                paymentMethodTypesOption={paymentMethodTypesOption}
                taxConfig={taxConfig}
                initialValues={initialValues}
                form_data={form_data}
                ChildrenMinAge={ChildrenMinAge}
                ChildrenMaxAge={ChildrenMaxAge}
                emptyRoom={emptyRoom}
                roomFields={roomFields}
                setChildrenMinAge={setChildrenMinAge}
                setChildrenMaxAge={setChildrenMaxAge}
                emptyPaymentDetail={emptyPaymentDetail}
                setErrors={setErrors}
                validateField={validateField}
                DormitoryRules={DormitoryRules}
                RoomFieldsRules={RoomFieldsRules}
                AdvanceDetailsRules={AdvanceDetailsRules}
                isEditMode={Boolean(id)}
                loadingFetch={loadingFetch}
            />
        </>
    );
};

export default EditRoomBooking;